import { Box, Paper, Typography, alpha, useTheme, Stack, Divider, Skeleton } from "@mui/material";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuHistory, LuShieldCheck, LuActivity, LuUsers, LuCircleAlert } from "react-icons/lu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, leftAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import RefreshButton from "../../components/button/RefreshButton";
import DateSelector from "../../components/input/DateSelector";
import CheckBox from "../../components/input/CheckBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { LoginHistory as LH } from "../../db_access/login_history";
import LoginHistoryFilters from "./LoginHistory/LoginHistoryFilters";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import MetricCard from "../../app_components/MetricCard";
import LoginGlobalView from "./LoginHistory/LoginGlobalView";

dayjs.extend(relativeTime);

const LoginHistory = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const { refresh, setRefresh } = useContextProvider();

  const [loginHistory, setLoginHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [globalView, setGlobalView] = useState(false);
  const gridRef = useRef(null);

  const [filters, setFilters] = useState({
    company_id: null,
    user_id: null,
    start_date: null,
    end_date: null,
    status: "all",
    datePreset: "all",
    limit: 500,
    offset: 0
  });

  const colDefs = useMemo(() => [
    {
      headerName: t("User Id"),
      field: "user_id",
      flex: 1.2,
      ...centerAlignColDef,
      cellStyle: { ...tok.gridCellText, ...centerAlignColDef.cellStyle },
      cellRenderer: (p) => (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
          <Typography sx={{ ...tok.gridCellText, textAlign: 'center' }}>{p.value}</Typography>
          {p.data.company_name && (
            <Typography sx={{ ...tok.gridCellText, color: tok.text.muted, fontSize: '0.8rem', textAlign: 'center' }}>
              ({p.data.company_name})
            </Typography>
          )}
        </Stack>
      )
    },
    {
      headerName: t("Timestamp"),
      field: "timestamp",
      width: 320,
      ...centerAlignColDef,
      cellStyle: { ...tok.gridCellText, ...centerAlignColDef.cellStyle },
      cellRenderer: (p) => (
        <Stack spacing={0} sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <Typography sx={{ ...tok.gridCellText }}>
            {dayjs(p.value).format("DD MMM YYYY, hh:mm A")}
          </Typography>
          <Typography sx={{ ...tok.gridCellText, color: tok.text.muted, fontSize: '0.8rem' }}>
            {dayjs(p.value).fromNow()}
          </Typography>
        </Stack>
      )
    },
    {
      headerName: t("Status"),
      field: "is_attempt_successful",
      width: 140,
      ...centerAlignColDef,
      cellStyle: { ...tok.gridCellText, ...centerAlignColDef.cellStyle },
      cellRenderer: (params) => {
        const success = params.value === 1 || params.value === true;
        const color = success ? '#10b981' : '#ef4444';
        return (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <Box sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: color,
            }} />
            <Typography sx={{
              ...tok.gridCellText,
              color: color,
            }}>
              {success ? t("Success") : t("Failed")}
            </Typography>
          </Stack>
        );
      }
    },
    {
      headerName: t("Ip Address"),
      field: "ip_address",
      width: 160,
      ...centerAlignColDef,
      cellStyle: { ...tok.gridCellText, ...centerAlignColDef.cellStyle },
      cellRenderer: p => (
        <Typography sx={{ ...tok.gridCellText, opacity: 0.8 }}>
          {p.value}
        </Typography>
      )
    },
    {
      headerName: t("Location"),
      field: "city",
      flex: 1,
      ...centerAlignColDef,
      cellStyle: { ...tok.gridCellText, ...centerAlignColDef.cellStyle },
      cellRenderer: p => (
        <Typography sx={{ ...tok.gridCellText }}>
          {p.data.city || t("Unknown")}{p.data.country ? `, ${p.data.country}` : ""}
        </Typography>
      )
    }
  ], [tok, t]);

  useEffect(() => {
    setLoading(true);
    LH.get_filtered({ ...filters, search: searchText })
      .then((res) => res.ok ? res.json() : [])
      .then((data) => {
        setLoginHistory(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filters, searchText, refresh]);

  useEffect(() => {
    // Analytics should reflect the broad search/status but ignore limit/offset
    const analyticsParams = { 
      ...filters, 
      search: searchText 
    };
    delete analyticsParams.limit;
    delete analyticsParams.offset;

    LH.get_analytics(analyticsParams)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => setAnalytics(data));
  }, [filters.company_id, filters.status, filters.user_id, filters.start_date, filters.end_date, searchText, refresh]);

  const topMetrics = useMemo(() => {
    if (!analytics) return [];
    const failureRate = analytics.total_logins > 0 ? ((analytics.failed_attempts / analytics.total_logins) * 100).toFixed(1) : 0;
    return [
      { id: 'total', title: t("Total Logins"), value: analytics.total_logins, unit: t("attempts"), icon: LuActivity, type: 'chain', accent: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
      { id: 'failed', title: t("Failed Attempts"), value: analytics.failed_attempts, unit: `${failureRate}%`, icon: LuCircleAlert, type: 'scope3', accent: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
      { id: 'users', title: t("Unique Users"), value: analytics.unique_users, unit: t("users"), icon: LuUsers, type: 'scope2', accent: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' }
    ];
  }, [analytics, t]);

  const getRowStyle = () => ({
    borderBottom: `1px solid ${tok.border.row}`
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap, animation: 'fadeIn 0.6s ease-out' }}>
      
      <Box sx={{
        ...tok.sectionHeading, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
        pl: 0,
        position: 'relative',
        pb: tok.sectionHeading.titlePb,
        borderBottom: tok.sectionHeading.borderBottom
      }}>
        <Box sx={{ 
            width: tok.sectionHeading.barWidth, 
            height: '24px', 
            bgcolor: tok.sectionHeading.barColor, 
            borderRadius: '2px' 
        }} />
        <Typography sx={{ 
            fontFamily: tok.sectionHeading.fontFamily,
            fontSize: tok.sectionHeading.fontSize,
            fontWeight: tok.sectionHeading.fontWeight,
            color: tok.sectionHeading.color
        }}>
          {t("Login History Audit")}
        </Typography>
      </Box>

      {/* 2. METRICS */}
      <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%' }}>
        {!analytics ? (
          Array.from(new Array(3)).map((_, i) => (
            <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <Paper sx={{ p: ORG_PALETTE.spacing.chartPad, width: 1, height: '140px', borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card, bgcolor: tok.bg.card }}>
                <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
                <Skeleton width="60%" height={32} />
              </Paper>
            </Box>
          ))
        ) : (
          topMetrics.map((m, i) => (
            <Box key={m.id} sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <MetricCard
                title={m.title}
                value={m.value}
                unit={m.unit}
                icon={m.icon}
                type={m.type}
                bg={m.bg}
                delay={i * 0.1}
              />
            </Box>
          ))
        )}
      </Box>

      {/* 3. FILTERS */}
      <Paper sx={{ p: tok.spacing.pageGap, borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card }}>
        <LoginHistoryFilters filters={filters} onFilterChange={setFilters} />
      </Paper>

      {/* 4. GRID */}
      <Paper variant="Table Container" sx={{ borderRadius: tok.radius.table, border: '1px solid', borderColor: tok.border.table, bgcolor: tok.bg.card, overflow: 'hidden', boxShadow: tok.shadow.card }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LuShieldCheck size={20} color={tok.bg.tabActive} />
              <Typography sx={{ 
                ...tok.tableTitle,
                fontSize: '18px', // High density adjustment
                fontWeight: 600
              }}>{t("Authentication Audit Log")}</Typography>
            </Box>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <SearchBox text={searchText} setText={setSearchText} placeholder={t("Trace security logs...")} width={300} />
            <ExportAsXLSXButton data={loginHistory} filename="login_audit.xlsx" />
            <RefreshButton setRefresh={() => setRefresh(r => !r)} />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box sx={{ p: 0 }}>
          <AgGrid
            ref={gridRef}
            columnDefs={colDefs}
            rowData={loginHistory}
            pagination={true}
            quickFilterText={searchText}
            domLayout="autoHeight"
            getRowStyle={getRowStyle}
            loading={loading}
            footerLeftContent={
              <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                <CheckBox checked={globalView} onChange={(e) => setGlobalView(e.target.checked)} label={t("Global Insights")} />
              </Box>
            }
          />
        </Box>
      </Paper>

      {/* 5. GLOBAL VIEW */}
      {globalView && (
        <Box sx={{ mt: tok.spacing.pageGap }}>
          <LoginGlobalView 
            loginHistory={loginHistory} 
            analytics={analytics}
            accentColor={tok.bg.tabActive}
          />
        </Box>
      )}

      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default LoginHistory;
